<template>
  <v-container>
    <div class="limiter-content pa-2 pa-lg-0">
      <v-row align="center" class="mb-2">
        <v-col>
          <div class="font-weight-bold mt-10 title-page" tabindex="0">Comprovantes informados</div>
          <p class="text-subtitle-1 grey--text mb-0">
            Comprovantes de pagamento que a Recuperanda informou à Administração Judicial sobre {{ lawyer ? 'o crédito dos credores que você representa' : 'o seu crédito' }}.
          </p>
        </v-col>
        <v-col cols="auto">
          <v-btn outlined color="primary" to="/home"><v-icon left>mdi-arrow-left</v-icon> Voltar</v-btn>
        </v-col>
      </v-row>

      <v-alert type="info" text border="left" class="mb-4">
        Esta página <strong>não indica pagamento pela plataforma</strong>. Ela mostra os comprovantes que a Recuperanda declarou já ter pago,
        para que você confira e, se for o caso, se manifeste. A análise é feita pela Administração Judicial.
      </v-alert>
      <ModuleClosedAlert module="site.payment_proofs" alert-class="mb-4" />

      <v-row v-if="loading" justify="center" class="my-10"><v-progress-circular indeterminate color="primary" size="56" /></v-row>

      <template v-else>
        <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
        <v-card v-else-if="!proofs.length" outlined class="rounded-lg pa-6 text-center grey--text text--darken-1">
          Nenhum comprovante informado pela Recuperanda até o momento.
        </v-card>

        <v-card v-for="proof in proofs" :key="proof.id" outlined class="rounded-lg mb-4">
          <div class="proof-header">
            <div>
              <div class="text-overline grey--text text--darken-1 proof-overline">Comprovante nº {{ proof.id }}</div>
              <div class="text-subtitle-1 font-weight-bold">{{ money(proof.paid_amount) }}</div>
              <div class="text-caption grey--text text--darken-1">
                <template v-if="lawyer && proof.creditor_name">{{ proof.creditor_name }} · </template>Informado em {{ date(proof.submitted_at || proof.created_at) }}
              </div>
            </div>
            <v-spacer />
            <v-chip small label :color="status(proof.status).color" text-color="white">{{ status(proof.status).label }}</v-chip>
          </div>
          <v-divider />
          <div class="pa-4">
            <v-row dense>
              <v-col v-if="proof.payment_date" cols="12" sm="6" md="3">
                <div class="objection-field__label">Data do pagamento</div>
                <div class="objection-field__value">{{ day(proof.payment_date) }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="objection-field__label">Motivo</div>
                <div class="objection-field__value">{{ proof.payment_reason || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="objection-field__label">Responsável pelo pagamento</div>
                <div class="objection-field__value">{{ proof.payer_name || proof.payment_responsible || '—' }}</div>
              </v-col>
              <v-col v-if="proof.process_number" cols="12" sm="6" md="3">
                <div class="objection-field__label">Processo</div>
                <div class="objection-field__value">{{ proof.process_number }}</div>
              </v-col>
              <v-col v-if="proof.document_url" cols="12">
                <v-btn :href="proof.document_url" target="_blank" rel="noopener noreferrer" small outlined color="primary">
                  <v-icon left small>mdi-file-document-outline</v-icon>Ver comprovante
                </v-btn>
              </v-col>
            </v-row>

            <ManifestationSection :manifestation="proof.manifestation" module-key="site.payment_proofs.contradictory"
              :endpoint="`${lawyer ? '/api/lawyer' : '/api/user'}/payment-proofs/${proof.id}/manifestation`"
              subject-label="este comprovante" overline="Comprovante informado" :lawyer="lawyer"
              :registration="proof.creditor_registration"
              question="Você concorda com o pagamento informado pela Recuperanda?" />
          </div>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios';
import ManifestationSection from '@/components/manifestation/ManifestationSection.vue';

const STATUS = {
  submitted: { label: 'Aguardando análise da AJ', color: 'blue-grey' },
  in_review: { label: 'Em análise pela AJ', color: 'indigo' },
  inconsistent: { label: 'Em conferência pela AJ', color: 'orange darken-2' },
  validated: { label: 'Validado pela AJ', color: 'green darken-1' },
};

// Aba "Comprovantes informados": comprovantes de pagamento informados pela Recuperanda sobre o crédito (credor e procurador).
export default {
  name: 'PaymentProofsInformed',
  components: { ManifestationSection },
  data: () => ({ proofs: [], loading: false, error: '' }),
  computed: {
    lawyer() { return !!this.$root.user && Number(this.$root.user.profile) === 4; },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const result = await axios.get(this.lawyer ? '/api/lawyer/payment-proofs' : '/api/user/payment-proofs');
        this.proofs = (result.data && result.data.content) || [];
      } catch (error) {
        const content = error.response && error.response.data && error.response.data.content;
        this.error = (content && content.error && content.error.message) || 'Não foi possível carregar os comprovantes.';
      } finally {
        this.loading = false;
      }
    },
    status(value) { return STATUS[value] || { label: 'Em análise pela AJ', color: 'blue-grey' }; },
    money(value) { return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); },
    date(value) { return value ? this.$moment(value).format('DD/MM/YYYY') : '—'; },
    day(value) { const [y, m, d] = String(value).slice(0, 10).split('-'); return d ? `${d}/${m}/${y}` : '—'; },
  },
};
</script>

<style>
.proof-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 16px; background: #f5f5f5; border-radius: 8px 8px 0 0; }
.proof-overline { line-height: 1.4 !important; }
</style>
